import { memo, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { ISprint } from "@hero/shared/models/sprint";
import { ITaskScores } from "@hero/shared/models/taskScore";
import {
    IUserExpectation,
    UserExpectation,
} from "@hero/shared/models/userExpectation";
import { IUserInfo } from "@hero/shared/models/userInfo";
import Notifications from "../../Notifications/Notifications";
import Notification from "../../common/icon/Notification";
import IconButton from "@mui/material/IconButton";
import Badge from "@mui/material/Badge";
import { INotification } from "@hero/shared/models/notification";

const ExpectationAndNotificationComponent = memo(
    ({
        currentSprint,
        taskScores,
        disciplinePoints,
    }: {
        currentSprint: ISprint;
        taskScores: ITaskScores;
        disciplinePoints: any;
    }) => {
        const userInfo: IUserInfo = useSelector(
            (state: any) => state.authState.user
        );
        const notifications: INotification[] = useSelector(
            (state: any) => state.notificationState.notifications ?? []
        );
        const sprints: ISprint[] = useSelector(
            (state: any) => state.sprintState.sprints ?? []
        );
        const activeSprint = sprints.find((s) => s.activeSprint);
        const notifCount = notifications.filter(
            (n) => n?.sprintID === activeSprint?._id && n.message
        ).length;

        const [open, setOpen] = useState(false);
        const panelRef = useRef<HTMLDivElement>(null);

        // Click outside to close
        useEffect(() => {
            if (!open) return;
            const handler = (e: MouseEvent) => {
                if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
                    setOpen(false);
                }
            };
            document.addEventListener("mousedown", handler);
            return () => document.removeEventListener("mousedown", handler);
        }, [open]);

        return (
            <div ref={panelRef} style={{ position: "fixed", top: 12, right: 16, zIndex: 1200 }}>
                {/* Bell icon */}
                <IconButton
                    onClick={() => setOpen(!open)}
                    sx={{
                        background: open ? "rgba(30,30,60,0.95)" : "rgba(30,30,60,0.8)",
                        borderRadius: "50%",
                        width: 44,
                        height: 44,
                        boxShadow: "0 2px 12px rgba(0,0,0,0.25)",
                        transition: "all 0.2s",
                        "&:hover": { background: "rgba(30,30,60,0.95)", transform: "scale(1.05)" },
                    }}
                >
                    <Badge
                        badgeContent={notifCount}
                        color="error"
                        max={99}
                        sx={{ "& .MuiBadge-badge": { fontSize: 10, height: 18, minWidth: 18 } }}
                    >
                        <Notification haveNotification={notifCount > 0} />
                    </Badge>
                </IconButton>

                {/* Dropdown panel */}
                {open && (
                    <div style={{
                        position: "absolute",
                        top: 52,
                        right: 0,
                        width: "min(380px, calc(100vw - 32px))",
                        maxHeight: "min(720px, calc(100vh - 80px))",
                        background: "#1e1e3c",
                        borderRadius: 12,
                        boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                        animation: "notifSlideIn 0.15s ease-out",
                    }}>
                        {/* Header */}
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "14px 16px",
                            borderBottom: "1px solid rgba(255,255,255,0.08)",
                        }}>
                            <span style={{ color: "#fff", fontSize: 15, fontWeight: 600 }}>
                                Thông báo
                                {notifCount > 0 && (
                                    <span style={{
                                        marginLeft: 8,
                                        fontSize: 12,
                                        fontWeight: 400,
                                        color: "rgba(255,255,255,0.5)",
                                    }}>
                                        ({notifCount})
                                    </span>
                                )}
                            </span>
                            <IconButton
                                size="small"
                                onClick={() => setOpen(false)}
                                sx={{ color: "rgba(255,255,255,0.5)", "&:hover": { color: "#fff" } }}
                            >
                                <span style={{ fontSize: 18 }}>✕</span>
                            </IconButton>
                        </div>

                        {/* Notification list */}
                        <div style={{ overflow: "auto", flex: 1 }}>
                            <Notifications />
                        </div>
                    </div>
                )}

                {/* Animation keyframes */}
                <style>{`
                    @keyframes notifSlideIn {
                        from { opacity: 0; transform: translateY(-8px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                `}</style>
            </div>
        );
    }
);

export default ExpectationAndNotificationComponent;
